<template>
  <div>
    <dx-grid
      :url="url"
      name="employer-childcare-benefit-requests"
      :columns="columns"
      :title="title"
      :actions="actions"
      :beforeSetupGrid="beforeSetupGrid"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      ref="grid"
    ></dx-grid>

    <!-- ── Details dialog ── -->
    <v-dialog v-model="detailsDialog" max-width="920" persistent scrollable>
      <v-card class="ecb-dialog">
        <!-- Header -->
        <div class="ecb-header">
          <div class="ecb-header-left">
            <div class="ecb-header-name">
              <span class="ecb-header-id">#{{ clickedRequest.iD }}</span>
              {{ clickedRequest.nameH || "Request" }}
            </div>
            <div class="ecb-header-ref">
              Account: {{ clickedRequest.accountID || "-" }} &nbsp;|&nbsp; Term:
              {{ clickedRequest.term || "-" }} &nbsp;|&nbsp; Saved:
              {{ formatDateTime(clickedRequest.dateSaved) }}
            </div>
          </div>
          <div class="ecb-header-right">
            <v-chip
              class="ecb-status-chip"
              color="white"
              :text-color="getStatusColor(clickedRequest.status)"
              small
            >
              <v-icon left small :color="getStatusColor(clickedRequest.status)">
                {{ getStatusIcon(clickedRequest.status) }}
              </v-icon>
              {{ clickedRequest.status || "—" }}
            </v-chip>
            <div class="ecb-sent-meta" v-if="clickedRequest.sentToEmployer">
              <v-icon
                small
                style="color:rgba(255,255,255,0.8);vertical-align:middle"
                >mdi-email-check-outline</v-icon
              >
              Sent {{ formatDateTime(clickedRequest.sentDate) }}
              <span v-if="clickedRequest.sentBy">
                by {{ clickedRequest.sentBy }}
              </span>
            </div>
          </div>
        </div>

        <div class="ecb-body">
          <v-layout row wrap class="ecb-sections">
            <!-- Left column: family / contact -->
            <v-flex xs12 sm6 class="ecb-col pr-3">
              <div class="ecb-section">
                <div class="ecb-section-title">Family Info</div>
                <div class="ecb-row">
                  <span class="ecb-label">Account ID</span>
                  <span class="ecb-val">{{
                    clickedRequest.accountID || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Name</span>
                  <span class="ecb-val">{{ clickedRequest.nameH || "-" }}</span>
                </div>

                <div class="ecb-row">
                  <span class="ecb-label">Home Phone</span>
                  <span class="ecb-val">{{
                    clickedRequest.homePhone || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Address</span>
                  <span class="ecb-val">{{
                    clickedRequest.address || "-"
                  }}</span>
                </div>
              </div>
            </v-flex>

            <!-- Right column: employer -->
            <v-flex xs12 sm6 class="ecb-col">
              <div class="ecb-section">
                <div class="ecb-section-title">Employer</div>
                <div class="ecb-row">
                  <span class="ecb-label">Employer Name</span>
                  <span class="ecb-val">{{
                    clickedRequest.employerName || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Employer Phone</span>
                  <span class="ecb-val">{{
                    clickedRequest.employerPhone || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Employer Email</span>
                  <span class="ecb-val">{{
                    clickedRequest.employerEmail || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Contact Person</span>
                  <span class="ecb-val">{{
                    clickedRequest.contactPerson || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Contact Person Title</span>
                  <span class="ecb-val">{{
                    clickedRequest.contactPersonTitle || "-"
                  }}</span>
                </div>
              </div>
            </v-flex>
          </v-layout>

          <!-- Editable panel -->
          <div class="ecb-update-panel">
            <div class="ecb-update-header">
              <v-icon small color="#2e7d32" class="mr-2"
                >mdi-pencil-circle</v-icon
              >
              <span>Update Request</span>
            </div>
            <v-layout row wrap class="ecb-update-content">
              <v-flex xs12 sm5 class="pr-2">
                <v-combobox
                  v-model="updateForm.status"
                  :items="statusOptions"
                  label="Status"
                  prepend-inner-icon="mdi-flag-variant"
                  dense
                  outline
                  hide-details
                ></v-combobox>
              </v-flex>
              <v-flex xs12 sm7 class="pl-2">
                <div
                  class="ecb-toggle-box"
                  v-bind:class="{
                    'ecb-toggle-box--on': updateForm.sentToEmployer,
                  }"
                  @click="
                    updateForm.sentToEmployer = !updateForm.sentToEmployer
                  "
                >
                  <div class="ecb-toggle-info">
                    <v-icon
                      small
                      :color="updateForm.sentToEmployer ? '#2e7d32' : '#9e9e9e'"
                      class="mr-2"
                      >{{
                        updateForm.sentToEmployer
                          ? "mdi-email-check-outline"
                          : "mdi-email-outline"
                      }}</v-icon
                    >
                    <div>
                      <div class="ecb-toggle-title">Sent to Employer</div>
                      <div
                        class="ecb-toggle-sub"
                        v-if="
                          updateForm.sentToEmployer && clickedRequest.sentBy
                        "
                      >
                        {{ clickedRequest.sentBy }} ·
                        {{ formatDateTime(clickedRequest.sentDate) }}
                      </div>
                      <div class="ecb-toggle-sub" v-else>
                        Mark once the employer has been contacted
                      </div>
                    </div>
                  </div>
                  <v-switch
                    v-model="updateForm.sentToEmployer"
                    color="success"
                    hide-details
                    class="mt-0 ecb-toggle-switch"
                    @click.native.stop
                  ></v-switch>
                </div>
              </v-flex>
              <v-flex xs12 class="mt-3">
                <v-textarea
                  v-model="updateForm.note"
                  label="Note"
                  prepend-inner-icon="mdi-note-text-outline"
                  dense
                  outline
                  rows="3"
                  hide-details
                ></v-textarea>
              </v-flex>
            </v-layout>
            <div class="ecb-update-actions">
              <v-btn color="success" :loading="saving" @click="saveRequest">
                <v-icon left small>mdi-content-save</v-icon>
                Save Changes
              </v-btn>
            </div>
          </div>

          <!-- Students table -->
          <div class="ecb-members-header">
            <span class="ecb-section-title mb-0">Students on Request</span>
            <span class="ecb-members-count">{{ students.length }}</span>
            <v-progress-circular
              v-if="loadingStudents"
              indeterminate
              color="success"
              size="18"
              width="2"
              class="ml-2"
            ></v-progress-circular>
          </div>

          <div class="ecb-members-wrap">
            <table class="ecb-members-table">
              <thead>
                <tr>
                  <th style="width:120px">Student ID</th>
                  <th>English Name</th>
                  <th>Hebrew Name</th>
                  <th style="width:120px">Class</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="student in students"
                  :key="student.id"
                  class="ecb-member-row"
                >
                  <td>{{ (student.studentID || "").trim() }}</td>
                  <td class="ecb-td-name">{{ student.engName }}</td>
                  <td>{{ student.hebName }}</td>
                  <td>{{ student.class }}</td>
                </tr>
                <tr v-if="!loadingStudents && !students.length">
                  <td colspan="4" class="ecb-td-empty">
                    No students found for this request.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Footer -->
        <div class="ecb-footer">
          <v-btn color="default" dark @click="closeDetailsDialog">Close</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="isAdmin()"
            color="error"
            outline
            @click="openDeleteDialog(clickedRequest)"
          >
            <v-icon left small>mdi-delete</v-icon>
            Delete Request
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── Delete confirmation dialog ── -->
    <v-dialog v-model="deleteDialog" max-width="500" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Delete Request?
        </v-card-title>
        <div class="body-1">
          You are about to permanently delete request
          <strong>#{{ requestToDelete.id }}</strong>
          <span v-if="requestToDelete.nameH">
            for <strong>{{ requestToDelete.nameH }}</strong></span
          >
          along with
          <strong>{{ requestToDelete.studentCount || 0 }}</strong>
          linked student(s).
        </div>
        <v-alert :value="true" type="warning" class="mt-3 mb-0" outline>
          This action cannot be undone. The request and all its students will be
          permanently lost.
        </v-alert>
        <v-card-actions class="pr-0 pt-4 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn :loading="deleting" color="error" @click="confirmDelete">
            <v-icon left small>mdi-delete</v-icon>
            Yes, Delete Permanently
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";

const STATUS_META = {
  new: { icon: "mdi-new-box", color: "#1976d2" },
  "in progress": { icon: "mdi-progress-clock", color: "#fdb400" },
  sent: { icon: "mdi-email-check-outline", color: "#2e7d32" },
  completed: { icon: "mdi-check-circle", color: "#2e7d32" },
};

function getStatusMeta(status) {
  const key = (status || "").trim().toLowerCase();

  return (
    STATUS_META[key] || { icon: "mdi-help-circle-outline", color: "#757575" }
  );
}

export default {
  data: () => ({
    title: "Employer Childcare Benefit Requests",
    url: "EmployerChildcareBenefitRequests",
    term: "",
    actions: [],
    detailsDialog: false,
    deleteDialog: false,
    loadingStudents: false,
    saving: false,
    deleting: false,
    clickedRequest: {},
    requestToDelete: {},
    students: [],
    statusOptions: ["New", "In Progress", "Sent", "Completed"],
    updateForm: {
      status: "",
      note: "",
      sentToEmployer: false,
    },
    columns: [
      {
        field: "statusIcon",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        allowHiding: false,
        allowSearch: false,
        cssClass: "width-25",
        headerFilter: {
          allowSearch: false,
          groupInterval: undefined,
          height: undefined,
          searchMode: "contains",
          width: undefined,
        },
        icon: (value, row) => value || getStatusMeta(row.status).icon,
        iconColor: (value, row) => getStatusMeta(row.status).color,
      },
      {
        label: "Account ID",
        field: "accountID",
        link: "Account View",
        template: "link-cell-template",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Name",
        field: "nameH",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Phone",
        field: "homePhone",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Address",
        field: "address",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Term",
        field: "term",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Employer Name",
        field: "employerName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Employer Phone",
        field: "employerPhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Employer Email",
        field: "employerEmail",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Contact Person",
        field: "contactPerson",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Contact Person Title",
        field: "contactPersonTitle",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Status",
        field: "status",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Students",
        field: "studentCount",
        type: "number",
        visible: true,
        width: 100,
        alignment: "center",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Sent To Employer",
        field: "sentToEmployer",
        type: "boolean",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Sent By",
        field: "sentBy",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Sent Date",
        field: "sentDate",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Note",
        field: "note",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Date Saved",
        field: "dateSaved",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        sortOrder: "desc",
        sortIndex: 0,
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),
  methods: {
    async beforeSetupGrid() {
      const [terms, currentRegID] = await Promise.all([
        this.loadTerms(),
        this.loadCurrentRegID(),
      ]);

      this.term = currentRegID || "";

      this.actions.push({
        text: "Term",
        type: "select",
        align: "left",
        clearable: true,
        placeholder: "All terms",
        values: terms,
        value: this.term,
        onSelected: this.onTermSelected,
      });

      this.buildUrl();
    },
    async loadCurrentRegID() {
      try {
        const response = await this.$http.get(
          "api/employerChildcareBenefitRequests/GetCurrentRegID"
        );

        return response && response.value ? response.value : response;
      } catch (e) {
        return "";
      }
    },
    buildUrl() {
      this.url = this.term
        ? "EmployerChildcareBenefitRequests?term=" +
          encodeURIComponent(this.term) +
          "&status="
        : "EmployerChildcareBenefitRequests?term=&status=";
    },
    onTermSelected(value) {
      this.term = value || "";

      this.buildUrl();

      if (this.$refs.grid) {
        this.$refs.grid.reloadDataSource(this.url);
      }
    },
    async loadTerms() {
      try {
        const response = await this.$http.get(
          "api/employerChildcareBenefitRequests/GetTerms"
        );

        return this.unwrap(response);
      } catch (e) {
        return [];
      }
    },
    unwrap(response) {
      const data = response && response.value ? response.value : response;

      return Array.isArray(data) ? data : [];
    },
    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];

      if (!isAdmin || isAdmin == "false") return false;

      return true;
    },
    getStatusIcon(status) {
      return getStatusMeta(status).icon;
    },
    getStatusColor(status) {
      return getStatusMeta(status).color;
    },
    formatDateTime(value) {
      if (!value) return "-";

      return moment(value).format("MM/DD/YYYY hh:mm:ss a");
    },
    async onRowClicked(row) {
      this.clickedRequest = Object.assign({}, row);

      this.updateForm.status = row.status || "";
      this.updateForm.note = row.note || "";
      this.updateForm.sentToEmployer = !!row.sentToEmployer;

      this.detailsDialog = true;

      await this.loadStudents();
    },
    async loadStudents() {
      const id = this.clickedRequest.id;

      if (!id && id !== 0) {
        this.students = [];

        return;
      }

      try {
        this.loadingStudents = true;

        const response = await this.$http.get(
          "api/employerChildcareBenefitRequests/GetStudents?requestId=" + id
        );

        this.students = this.unwrap(response);
      } catch (e) {
        this.students = [];
        this.$notify({
          text: "Failed to load students",
          group: "notification",
          type: "error",
        });
      } finally {
        this.loadingStudents = false;
      }
    },
    async saveRequest() {
      const id = this.clickedRequest.id;

      if (!id && id !== 0) return;

      const sentToEmployer = this.updateForm.sentToEmployer;
      const wasSent = !!this.clickedRequest.sentToEmployer;

      const body = {
        iD: id,
        status: this.updateForm.status,
        note: this.updateForm.note,
        sentToEmployer: sentToEmployer,
      };

      if (sentToEmployer && !wasSent) {
        body.sentBy = this.$store.state.authentication.user;
        body.sentDate = moment().format("YYYY-MM-DD HH:mm:ss");
      } else {
        body.sentBy = this.clickedRequest.sentBy;
        body.sentDate = this.clickedRequest.sentDate;
      }

      try {
        this.saving = true;

        await this.$http.post(
          "api/employerChildcareBenefitRequests/Save",
          body
        );

        this.clickedRequest.status = body.status;
        this.clickedRequest.note = body.note;
        this.clickedRequest.sentToEmployer = body.sentToEmployer;
        this.clickedRequest.sentBy = body.sentBy;
        this.clickedRequest.sentDate = body.sentDate;

        this.$notify({
          text: "Request updated successfully",
          group: "notification",
          type: "success",
        });

        if (this.$refs.grid) {
          this.$refs.grid.reloadGrid();
        }
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to update request"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
    openDeleteDialog(request) {
      if (!this.isAdmin()) return;

      this.requestToDelete = Object.assign({}, request);
      this.deleteDialog = true;
    },
    async confirmDelete() {
      const id = this.requestToDelete.id;

      if ((!id && id !== 0) || !this.isAdmin()) return;

      try {
        this.deleting = true;

        await this.$http.post(
          "api/employerChildcareBenefitRequests/Delete?id=" + id
        );

        this.$notify({
          text: "Request deleted successfully",
          group: "notification",
          type: "success",
        });

        this.deleteDialog = false;
        this.detailsDialog = false;

        if (this.$refs.grid) {
          this.$refs.grid.reloadGrid();
        }
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to delete request"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.deleting = false;
      }
    },
    getErrorMessage(error, fallback) {
      if (error && error.Message) return error.Message;
      if (error && error.message) return error.message;
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.Message
      )
        return error.response.data.Message;

      return fallback;
    },
    closeDetailsDialog() {
      this.detailsDialog = false;
      this.clickedRequest = {};
      this.students = [];
      this.updateForm.status = "";
      this.updateForm.note = "";
      this.updateForm.sentToEmployer = false;
    },
  },
};
</script>

<style>
/* ── Dialog shell ── */
.ecb-dialog {
  background: #fff;
  border-radius: 10px;
  display: flex !important;
  flex-direction: column;
  max-height: 92vh;
  overflow: hidden;
}

/* ── Header ── */
.ecb-header {
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: space-between;
  background: #4caf50;
  color: #fff;
  padding: 20px 24px 16px;
}
.ecb-header-id {
  font-size: 18px;
  font-weight: 700;
  margin-right: 8px;
  opacity: 0.85;
}
.ecb-header-name {
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: baseline;
  gap: 2px;
}
.ecb-header-ref {
  font-size: 12px;
  opacity: 0.8;
  margin-top: 6px;
}
.ecb-header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.ecb-status-chip.v-chip {
  font-weight: 600;
  letter-spacing: 0.3px;
}
.ecb-sent-meta {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  text-align: right;
}

/* ── Body ── */
.ecb-body {
  padding: 20px 24px 0;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
.ecb-sections {
  margin-bottom: 4px;
}
.ecb-col {
  padding-bottom: 4px;
}

/* ── Section ── */
.ecb-section {
  margin-bottom: 18px;
}
.ecb-section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #2e7d32;
  border-bottom: 2px solid #e8f5e9;
  padding-bottom: 4px;
  margin-bottom: 8px;
}

/* ── Label / value row ── */
.ecb-row {
  display: flex;
  align-items: flex-start;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13.5px;
  line-height: 1.4;
}
.ecb-row:last-child {
  border-bottom: none;
}
.ecb-label {
  flex: 0 0 168px;
  color: #757575;
  padding-right: 10px;
}
.ecb-val {
  flex: 1;
  color: #212121;
  font-weight: 500;
  word-break: break-word;
}

/* ── Update panel ── */
.ecb-update-panel {
  margin: 0 0 18px;
  padding: 14px 16px 16px;
  border: 2px solid #a5d6a7;
  border-radius: 10px;
  background: #f3fbf4;
}
.ecb-update-header {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #1b5e20;
  margin-bottom: 2px;
}
.ecb-update-content {
  margin-top: 10px;
}
.ecb-update-actions {
  display: flex;
  justify-content: flex-start;
  margin-top: 16px;
}

/* ── Sent-to-employer toggle box ── */
.ecb-toggle-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  min-height: 40px;
  padding: 6px 10px 6px 14px;
  border: 1px solid #cfe8d1;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.ecb-toggle-box:hover {
  border-color: #a5d6a7;
}
.ecb-toggle-box--on {
  border-color: #4caf50;
  background: #f1f8f2;
}
.ecb-toggle-info {
  display: flex;
  align-items: center;
  min-width: 0;
}
.ecb-toggle-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #212121;
  line-height: 1.2;
}
.ecb-toggle-sub {
  font-size: 11.5px;
  color: #757575;
  margin-top: 2px;
}
.ecb-toggle-switch {
  flex: 0 0 auto;
  margin-left: 10px;
}
.ecb-toggle-switch .v-input__slot {
  margin-bottom: 0 !important;
}

/* ── Students table ── */
.ecb-members-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  margin-top: 6px;
}
.ecb-members-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #4caf50;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 10px;
  min-width: 24px;
  height: 20px;
  padding: 0 7px;
}
.ecb-members-wrap {
  border: 1px solid #dcedc8;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
}
.ecb-members-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  font-size: 13.5px;
}
.ecb-members-table thead th {
  background: #f1f8e9;
  color: #33691e;
  font-weight: 700;
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 10px 16px;
  text-align: left;
  border-bottom: 2px solid #dcedc8;
  white-space: nowrap;
}
.ecb-members-table tbody td {
  padding: 10px 16px;
  color: #212121;
  border-bottom: 1px solid #f1f8e9;
  white-space: nowrap;
}
.ecb-members-table tbody tr:last-child td {
  border-bottom: none;
}
.ecb-member-row:hover td {
  background: #f9fbe7;
}
.ecb-td-name {
  font-weight: 600;
}
.ecb-td-empty {
  text-align: center;
  padding: 20px 16px !important;
  color: #9e9e9e;
  font-style: italic;
}

/* ── Footer ── */
.ecb-footer {
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 14px 24px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
}
</style>
